// Is the trip weather email backend deployed on this branch?
//
// Same shape as shareTrip.mjs's gate, but for a different reason. That one
// reads a Function URL, which is absent from amplify_outputs.json until its
// Lambda deploys. This feature has no Function URL -- it is reached entirely
// through AppSync, a GSI query and a custom mutation -- so there is no key that
// naturally appears when its backend lands, and amplify/backend.ts publishes
// this flag on purpose to be that key.
//
// It matters because AppSync rejects a WHOLE query that names a field the
// branch's schema does not have. Without the gate, opening the modal on a
// branch that has not deployed would fail the request rather than degrade, and
// the frontend could not ship ahead of the schema at all. amplify_outputs.json
// is regenerated per branch at deploy time, so a branch without the backend
// simply has no key here and the button stays hidden.

import outputs from '../amplify_outputs.json';
import {
  listTripWeatherSubscriptionsForItem,
  deleteTripWeatherSubscription,
  updateTripWeatherSubscription,
} from './Database.mjs';

export function tripWeatherEmailAvailable() {
  return Boolean(outputs?.custom?.tripWeatherEmailEnabled);
}

// Best-effort cleanup when a trip's last leg goes. BEST-EFFORT is the operative
// word and the reason this swallows: the authoritative rule lives in
// TaskPogoTripWeatherMail, which deletes any subscription whose 'g:' key no
// longer resolves to a trip. That covers every path this cannot -- an import
// replace, an item cascade, a direct AppSync call -- so failing here must never
// block the delete the user actually asked for.
//
// Returns the number of rows removed, for a caller that wants to say so.
// The subscriptions a merge onto `newKey` touches, keeper first.
//
// tripShareRowsForMerge's counterpart, split out for the same reason: the
// dialog warns about a schedule that is about to be cancelled, and it has to
// pick the same keeper the repoint will.
export function tripWeatherRowsForMerge(rows, oldKeys, newKey) {
  const keys = [newKey, ...(oldKeys || [])];
  return keys
    .flatMap((key) => (rows || []).filter((row) => row?.groupKey === key))
    .filter((row, index, all) => all.findIndex((r) => r?.id === row?.id) === index);
}

// Move the weather-email schedules of two trips that have just become one onto
// `newKey`, and keep exactly ONE.
//
// The share rule, for a sharper reason. WeatherEmailModal reads a group's
// subscription with getTripWeatherSubscription, which answers with items[0], so
// a second row on the same key is not merely invisible -- it is a daily email,
// possibly to a different address, that nobody has any way to cancel. The
// daemon enqueues per row, so the trip would also be briefed twice a day.
//
// Doing nothing is worse still: trip-weather-mail DELETES any subscription
// whose 'g:' key no longer resolves to a trip, so the absorbed trip's schedule
// would disappear on the next fire, silently, from a real person's inbox.
//
// The row on `newKey` survives, else the first of `oldKeys` -- callers pass the
// trip the user is standing on first. `tripName` is a display snapshot on the
// row, so it is refreshed with the combined name. `startDate` / `stopDate` are
// NOT: the stored window may be one the user narrowed by hand, and moving it
// would also strand `nextSendLocalDate`, which only the daemon advances. The
// caller says so instead.
//
// Best-effort and swallowing, like its neighbour. Returns
// { repointed, cancelled, cancelledEmails } so the caller can name what stopped.
export async function repointTripWeatherSubscriptions(itemId, oldKeys, newKey, tripName) {
  const result = { repointed: 0, cancelled: 0, cancelledEmails: [] };
  if (!tripWeatherEmailAvailable()) return result;
  try {
    const rows = await listTripWeatherSubscriptionsForItem(itemId);
    const matching = tripWeatherRowsForMerge(rows, oldKeys, newKey);
    if (matching.length === 0) return result;

    const [keep, ...extras] = matching;
    for (const row of extras) {
      await deleteTripWeatherSubscription(row.id);
      result.cancelled += 1;
      if (row.email) result.cancelledEmails.push(row.email);
    }
    if (keep.groupKey !== newKey || (tripName && keep.tripName !== tripName)) {
      await updateTripWeatherSubscription({
        id: keep.id,
        groupKey: newKey,
        ...(tripName ? { tripName } : {}),
      });
      result.repointed += 1;
    }
    return result;
  } catch (err) {
    console.error('Combined the trips but could not update their weather email schedule', err);
    return result;
  }
}

export async function removeTripWeatherSubscriptionsForGroup(itemId, groupKey) {
  if (!tripWeatherEmailAvailable()) return 0;
  try {
    const rows = await listTripWeatherSubscriptionsForItem(itemId);
    const matching = rows.filter((row) => row.groupKey === groupKey);
    for (const row of matching) {
      await deleteTripWeatherSubscription(row.id);
    }
    return matching.length;
  } catch (err) {
    console.error('Deleted the trip but could not remove its weather email subscription', err);
    return 0;
  }
}
